import {Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DocumentsService} from '../../../services/documents.service';
import {NgForm} from '@angular/forms';
import {ApiClientService} from '../../../services/api-client.service';
import {UrlService} from '../../../services/url.service';
import {ShareType} from '../../../types/documents/share-types';
import {Subject} from 'rxjs/Subject';
import {takeUntil} from 'rxjs/operators';

declare const toastr: any;
declare const $: any;
declare const swal: any;

@Component({
    selector: 'create-folder-modal',
    templateUrl: './create-folder-modal.component.html',
    styleUrls: ['./create-folder-modal.component.scss']
})
export class CreateFolderModalComponent implements OnInit, OnDestroy {
    private dayInMilliseconds = 1000 * 60 * 60 * 24;

    public data: IFolder = new IFolder();
    public isPermissionsLoading: boolean = false;
    public isCorporationLoading: boolean = false;
    public permissionsList: Array<any> = [];
    public isShowPreloader: boolean = false;
    public corporationNumbers: Array<any> = [];
    public buildings: Array<any> = [];
    public isShared: boolean = false;

    private shareTypeValue: ShareType = null;

    public shareTypes = ShareType;
    public shareTypeOptions = Object.keys(ShareType).map(key => ({name: key, value: ShareType[key]}));

    @ViewChild('folderNameInput') folderNameInput: ElementRef;

    currentShareTypeOptions: Array<any> = this.shareTypeOptions;
    private userGroups: Array<any> = [];
    private parentPermissions = [];
    private ngUnsubscribe = new Subject();
    private _visibleFrom: Date;
    private _visibleTo: any;

    haveaParent = false;
    hasAcceptableDates: boolean;

    constructor(public api: ApiClientService,
                public ds: DocumentsService,
                public urlService: UrlService) {
    }

    get visibleFrom(): Date {
        return this._visibleFrom;
    }

    set visibleFrom(value: Date) {
        this._visibleFrom = value;
        if (this._visibleTo) {
            if (this._visibleFrom > this._visibleTo) {
                const visibleFrom = new Date(this._visibleFrom.valueOf()).getTime();
                this._visibleTo = new Date(visibleFrom + this.dayInMilliseconds);
            }
        }
    }

    get visibleTo(): any {
        return this._visibleTo;
    }

    set visibleTo(value: any) {
        this._visibleTo = value;
        if (this._visibleFrom) {
            if (this._visibleFrom > this._visibleTo) {
                const visibleTo = new Date(this._visibleTo.valueOf()).getTime();
                this._visibleFrom = new Date(visibleTo - this.dayInMilliseconds);
            }
        }
    }

    ngOnInit() {
        this.ds.corporationsListLoaded
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data) => {
                if (data)
                    this.getCorporationNumberList();
                else
                    this.isCorporationLoading = true;
            });

        this.loadPermissions();
        this.urlService.modalOpened.emit();
    }

    onNameChange(value) {
        value = value.trim();

        if (!!value) {
            this.data.name = value;
        } else {
            this.data.name = null;
        }
    }

    onSubmit(form: NgForm, e) {
        e.preventDefault();

        let isInvalid = false;

        if (this.isShared && !this.shareType) {
            toastr.warning('Share option type should be selected');
            isInvalid = true;
        }

        if (this.shareType === ShareType.Groups && !(this.data.userGroups && this.data.userGroups.length)) {
            toastr.warning('Group should not be blank.');
            isInvalid = true;
        }

        if (!this.data.corporationNumbers.length) {
            toastr.warning('Corporation Numbers should not be blank.');
            isInvalid = true;
        }

        if (!this.data.buildings.length) {
            toastr.warning('Buildings should not be blank.');
            isInvalid = true;
        }

        if (!this.data.name) {
            toastr.warning('Name should not be blank.');
            isInvalid = true;
        }

        if (this.shareType === ShareType.Residents && !(this.data.permissions && this.data.permissions.length)) {
            toastr.warning('Occupancy type should not be blank.');
            isInvalid = true;
        }

        if (isInvalid) {
            return;
        }

        const data = {...this.data};
        data.permissions = this.data.permissions.map((item) => {
            return {
                permission: item.id
            };
        });
        data.userGroups = this.data.userGroups.map(g => ({
            bdId: g.id,
            name: g.itemName
        }));
        if (this.hasAcceptableDates) {
            if (this.visibleFrom) {
                const acceptableFrom = new Date(this.visibleFrom.valueOf());
                acceptableFrom.setHours(0);
                acceptableFrom.setMinutes(0);
                acceptableFrom.setSeconds(0);
                data.acceptableFrom = acceptableFrom.toISOString();
            }
            if (this.visibleTo) {
                const acceptableTo = new Date(this.visibleTo.valueOf());
                acceptableTo.setHours(23);
                acceptableTo.setMinutes(59);
                acceptableTo.setSeconds(59);
                data.acceptableTo = acceptableTo.toISOString();
            }
        }
        this.isShowPreloader = true;
        this.ds.createFolder(data).subscribe(() => {
            this.isShowPreloader = false;
            this.ds.hideCreateFolderModal();
        });
    }

    private loadPermissions(flag?): void {
        this.isPermissionsLoading = true;
        this.ds.getParentPermissions().subscribe(({
                                                      permissionsList,
                                                      parentPermission,
                                                      documentsPermissions,
                                                      userGroups,
                                                      isPublic
                                                  }) => {
            this.isPermissionsLoading = false;
            this.permissionsList = permissionsList.map(this.ds.parsePermission);
            this.parentPermissions = parentPermission.map(this.ds.parsePermission);
            this.folderNameInput.nativeElement.focus();

            if (documentsPermissions !== null || userGroups !== null || isPublic !== null) {
                if (documentsPermissions !== null && documentsPermissions.length) {
                    this.data.permissions = documentsPermissions.map(this.ds.parsePermission);
                }
                if (userGroups !== null) {
                    this.userGroups = userGroups.map(role => ({
                        id: role.bdId,
                        itemName: role.name
                    }));
                    this.data.userGroups = JSON.parse(JSON.stringify(this.userGroups));
                }
                this.initSareValues({documentsPermissions, userGroups, isPublic});
            } else {
                this.userGroups = JSON.parse(JSON.stringify(this.ds.userGroups));
            }

            if (flag) {
                this.isShared = flag;
            }
        });
    }

    initSareValues(parentPermissions) {
        if (parentPermissions.isPublic) {
            this.shareTypeValue = ShareType.Public;
        } else if (parentPermissions.documentsPermissions.length) {
            this.shareTypeValue = ShareType.Residents;
        } else if (parentPermissions.userGroups.length) {
            this.shareTypeValue = ShareType.Groups;
        } else {
            this.shareTypeValue = null;
        }
        this.isShared = this.shareTypeValue !== null;
        this.currentShareTypeOptions = this.shareTypeOptions.filter(item => item.value == this.shareTypeValue);
    }

    set shareType(value: ShareType) {
        this.shareTypeValue = value;
        this.isShared = value !== null;

        this.data.isPublic = this.shareTypeValue === ShareType.Public;

        if (this.shareTypeValue !== ShareType.Residents) {
            this.data.permissions = []; // force reset permissions
        } else if (!this.data.permissions.length) {
            this.data.permissions = [...this.parentPermissions]; // set parent permissions as initial state
        }

        if (this.shareTypeValue !== ShareType.Groups) {
            this.data.userGroups = []; // force reset group sharing
        }
    }

    get shareType(): ShareType {
        return this.shareTypeValue;
    }

    onPermissionsChange(data): void {
        this.data.permissions = data;
    }

    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
        setTimeout(() => this.ds.hideCreateFolderModal(), 100);
    }

    getBuildingsForSelectedCorporations(event?, eventType?) {
        this.buildings = [];

        if (!this.haveaParent) {
            this.data.corporationNumbers.forEach(corp => {
                this.ds.buildingsForUpdate.forEach(building => {
                    if ((<any>building).corporationNumber == corp.id)
                        this.buildings.push({
                            ...building,
                            itemName: building.name,
                        });
                });
            });
        } else {
            this.getParentBuildings();
        }

        if (eventType == 'deselect')
            this.data.buildings = this.data.buildings.filter(b => b.corporationNumber != event.id);
        else if (eventType == 'deselectAll')
            this.data.buildings = [];

        if (this.buildings.length === 1)
            this.data.buildings = JSON.parse(JSON.stringify(this.buildings));
    }

    getParentBuildings() {
        const parentBuildings = this.ds.getParentBuildings();

        if (!parentBuildings.length) return;

        this.buildings = [];
        this.data.buildings = [];

        if (!this.data.corporationNumbers.length) return;

        parentBuildings.map(building => {
            this.buildings.push({
                ...building,
                itemName: building.name,
            });
        });
    }

    setDataBuildings() {
        this.data.buildings = JSON.parse(JSON.stringify(this.buildings));
        this.loadPermissions(true);
    }

    getCorporationNumberList() {
        this.corporationNumbers = [];
        const availableCorporationNumbers = this.ds.buildingsForUpdate.reduce((acc, next) => {
            acc[next.corporationNumber] = true;
            return acc;
        }, {});
        this.corporationNumbers = this.ds.corporations
            .filter(corporation => availableCorporationNumbers.hasOwnProperty(corporation.id))
            .map(item => ({
            ...item,
            itemName: item.name,
            disabled: item.disabled
        }));
        const parentCorporations = this.ds.getParentCorporations(this.corporationNumbers);
        this.data.corporationNumbers = parentCorporations && parentCorporations.length > 0 ? parentCorporations : [];
        if ((!parentCorporations || parentCorporations.length < 1) && this.corporationNumbers.length === 1) {
            this.data.corporationNumbers = JSON.parse(JSON.stringify(this.corporationNumbers));
            this.getBuildingsForSelectedCorporations();
        }
        if (parentCorporations.length) {
            this.haveaParent = true;
            this.corporationNumbers = this.corporationNumbers.filter(item => {
                let hasMatchedCorporation = false;
                parentCorporations.forEach(element => {
                    if (String(item.id) === String(element.id)) {
                        hasMatchedCorporation = true;
                    }
                });
                return hasMatchedCorporation;
            });
            this.getParentBuildings();
            this.setDataBuildings();
        }
        this.isCorporationLoading = false;
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    shareTypeChange(selectedShareType: string) {
        this.hasAcceptableDates = selectedShareType !== null;
    }
}

class IFolder {
    corporationNumbers: Array<any> = [];
    buildings: Array<any> = [];
    name: string = null;
    permissions: Array<any> = [];
    userGroups: Array<any> = [];
    isPublic: boolean;
    acceptableFrom?: string;
    acceptableTo?: string;
}



// WEBPACK FOOTER //
// /app/src/app/views/documents/create-folder-modal/create-folder-modal.component.ts